import { reportPlatformIconUrl } from "./dashboardReportPlatformIcons"
import { formatPlatformLabel } from "./dashboardReportFormatters"
import { SOCIAL_PERFORMANCE_BENCHMARK_REPORT_STYLES } from "./socialPerformanceBenchmarkReport"
import {
  buildDashboardReportIndexEntries,
  getReportChartAnchor,
  renderDashboardReportIndexHtml,
} from "./dashboardReportIndex"

/** PDF uses export tables instead of clipped DOM screenshots for these charts. */
const CHART_IDS_PREFER_DATA_VISUAL = new Set([
  "topPosts",
  "topStories",
  "topPerformingAds",
  "topPaidAds",
  "topObjectives",
  "topCampaigns",
  "advertisingPerformance",
  "audienceInsights",
  "socialGoalsOverview",
  "goalPerformance",
  "monthlySocialPerformance",
  "socialPerformanceBenchmark",
])

const CHART_IDS_WITH_API_RASTER = new Set([
  "messages",
  "messageSentiment",
  "alerts",
  "timeOfDay",
  "audienceInsights",
  "platformPerformanceComparison",
  "paidMediaPerformance",
])

const CHART_IDS_SIDE_BY_SIDE_REPORT = new Set([
  "platformPerformanceComparison",
  "campaignPerformance",
])

/** Charts exported from API / tables — no DOM scroll or tab navigation during export. */
export function chartReportSkipsDomCapture(chartId) {
  if (CHART_IDS_PREFER_DATA_VISUAL.has(chartId)) return true
  if (chartId === "socialGoalsOverview") return true
  if (CHART_IDS_WITH_API_RASTER.has(chartId)) return true
  return false
}

export function chartPrefersReportDataTables(chartId) {
  return CHART_IDS_PREFER_DATA_VISUAL.has(chartId)
}

/** Still capture a dashboard widget that sits beside an API-raster chart. */
export function chartNeedsCompanionDomCapture() {
  return false
}

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

export function isValidChartSnapshot(dataUrl) {
  return (
    typeof dataUrl === "string" &&
    dataUrl.startsWith("data:image/") &&
    dataUrl.length > 800
  )
}

function renderMetrics(metrics) {
  if (!metrics?.length) return ""
  return `<div class="report-metrics">
    ${metrics
      .map(
        (m) => `
      <div class="report-metric">
        <div class="report-metric-label">${escapeHtml(m.label)}</div>
        <div class="report-metric-value">${escapeHtml(m.value)}</div>
        ${m.hint ? `<div class="report-metric-hint">${escapeHtml(m.hint)}</div>` : ""}
      </div>`,
      )
      .join("")}
  </div>`
}

function isNumericColumnType(type) {
  return (
    type === "number" ||
    type === "percent" ||
    type === "percentRaw" ||
    type === "currency" ||
    type === "compact" ||
    type === "metricDelta" ||
    type === "growth"
  )
}

function growthToneFromText(text) {
  const t = String(text ?? "").replace(/\s/g, "")
  if (!t || t === "—" || t === "-" || t === "–") return "empty"
  if (t.startsWith("+")) return "up"
  if (t.startsWith("-")) return "down"
  return "empty"
}

function renderPostCaptionCell(rawCell, displayCell) {
  const data =
    rawCell && typeof rawCell === "object"
      ? rawCell
      : { title: "", copy: displayCell || rawCell || "" }
  const title = String(data.title ?? "").trim()
  const copy = String(data.copy ?? "").trim()
  const thumb = String(data.thumb || data.media || "").trim()
  if (!title && !copy && !thumb) return `<td class="report-post-td">—</td>`
  const titleMarkup = title
    ? `<span class="report-post-title">${escapeHtml(title)}</span>`
    : ""
  const copyMarkup = copy
    ? `<span class="report-post-copy">${escapeHtml(copy)}</span>`
    : ""
  const thumbMarkup = thumb
    ? `<img class="report-post-thumb" src="${escapeHtml(thumb)}" alt="" />`
    : ""
  return `<td class="report-post-td">
    <div class="report-post-cell${thumb ? " report-post-cell--media" : ""}">
      ${thumbMarkup}
      <div class="report-post-text">${titleMarkup}${copyMarkup}</div>
    </div>
  </td>`
}

function renderCompetitorCell(rawCell, displayCell) {
  const data =
    rawCell && typeof rawCell === "object"
      ? rawCell
      : { name: displayCell || rawCell, subBrand: "", isYou: false, isBrandAverage: false }
  const name = String(data.name ?? "").trim() || "—"
  const subBrand = String(data.subBrand ?? "").trim()
  const youTag = data.isYou ? `<span class="report-you-tag">YOU</span>` : ""
  const avgTag = data.isBrandAverage
    ? `<span class="report-brand-avg-tag">Brand Average</span>`
    : ""
  const subMarkup = subBrand
    ? `<span class="report-competitor-sub">${escapeHtml(subBrand)}</span>`
    : ""
  return `<td class="report-competitor-td">
    <div class="report-competitor-cell">
      <span class="report-competitor-name">${escapeHtml(name)}${youTag}${avgTag}</span>
      ${subMarkup}
    </div>
  </td>`
}

function renderTable(table) {
  if (!table?.columns?.length || !table?.rows?.length) return ""
  const header = table.columns
    .map((c, colIndex) => {
      const numeric = isNumericColumnType(table.columnTypes?.[colIndex])
      const classes = numeric ? ' class="report-table-cell--num"' : ""
      return `<th${classes} title="${escapeHtml(c)}">${escapeHtml(c).replace(/\n/g, "<br />")}</th>`
    })
    .join("")
  const platformColIndex = table.columns.findIndex((c) => /platform|^plat\.?$/i.test(String(c)))
  const columnTypes = table.columnTypes || []
  const rawRows = table.rawRows || table.rows
  const wrapClass = table.className ? ` ${table.className}` : ""
  const body = rawRows
    .map((rawRow, rowIndex) => {
      const displayRow = table.rows[rowIndex] || rawRow
      const competitorRaw = rawRow.find((_, i) => columnTypes[i] === "competitor")
      const rowClasses = []
      if (competitorRaw?.isYou) rowClasses.push("report-table-row--you")
      if (competitorRaw?.isBrandAverage) rowClasses.push("report-table-row--average")
      const trAttr = rowClasses.length ? ` class="${rowClasses.join(" ")}"` : ""
      return `<tr${trAttr}>${rawRow
        .map((rawCell, colIndex) => {
          const cellType = columnTypes[colIndex]
          if (cellType === "competitor") {
            return renderCompetitorCell(rawCell, displayRow[colIndex])
          }
          if (cellType === "postCaption") {
            return renderPostCaptionCell(rawCell, displayRow[colIndex])
          }
          if (cellType === "link" && rawCell) {
            const url = typeof rawCell === "object" ? String(rawCell.url || "") : String(rawCell)
            const label =
              typeof rawCell === "object" && rawCell.label
                ? String(rawCell.label)
                : "View post"
            if (!url) return `<td>—</td>`
            return `<td><a class="report-table-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a><span class="report-table-link-url">${escapeHtml(url)}</span></td>`
          }
          if (cellType === "platformIcon" && rawCell) {
            const iconUrl = reportPlatformIconUrl(rawCell)
            const iconMarkup = iconUrl
              ? `<img class="report-platform-icon" src="${escapeHtml(iconUrl)}" alt="${escapeHtml(
                  formatPlatformLabel(rawCell),
                )}" data-platform="${escapeHtml(rawCell)}" width="16" height="16" />`
              : escapeHtml(formatPlatformLabel(rawCell))
            return `<td class="report-platform-cell--icon-only">${iconMarkup}</td>`
          }
          const cell = displayRow[colIndex] != null && displayRow[colIndex] !== ""
            ? displayRow[colIndex]
            : rawCell
          if (cellType === "platform" || colIndex === platformColIndex) {
            const iconSource = rawCell || cell
            const iconUrl = reportPlatformIconUrl(iconSource)
            const label = formatPlatformLabel(iconSource)
            const iconMarkup = iconUrl
              ? `<img class="report-platform-icon" src="${escapeHtml(iconUrl)}" alt="${escapeHtml(
                  label,
                )}" data-platform="${escapeHtml(iconSource)}" width="18" height="18" />`
              : ""
            return `<td title="${escapeHtml(label)}" data-platform-label="${escapeHtml(label)}"><span class="report-platform-cell">${iconMarkup}<span>${escapeHtml(label)}</span></span></td>`
          }
          if (cellType === "growth") {
            const growthText = String(
              displayRow[colIndex] != null && displayRow[colIndex] !== ""
                ? displayRow[colIndex]
                : rawCell ?? "—",
            )
            const tone = growthToneFromText(growthText)
            return `<td class="report-table-cell--num report-table-cell--growth report-table-cell--growth-${escapeHtml(
              tone,
            )}">${escapeHtml(growthText)}</td>`
          }
          if (cellType === "metricDelta") {
            const metric =
              rawCell && typeof rawCell === "object"
                ? rawCell
                : { value: cell, delta: "", deltaTone: "empty" }
            const tone = metric.deltaTone || "empty"
            const deltaMarkup = metric.delta
              ? `<span class="report-metric-cell-delta report-metric-cell-delta--${escapeHtml(tone)}">${escapeHtml(
                  metric.delta,
                )}</span>`
              : ""
            return `<td class="report-table-cell--num report-table-cell--metric">
              <div class="report-metric-cell">
                <span class="report-metric-cell-value">${escapeHtml(metric.value ?? "—")}</span>
                ${deltaMarkup}
              </div>
            </td>`
          }
          if (cellType === "date") {
            return `<td class="report-table-cell--date">${escapeHtml(cell)}</td>`
          }
          const numericClass = isNumericColumnType(cellType) ? ' class="report-table-cell--num"' : ""
          return `<td${numericClass}>${escapeHtml(cell)}</td>`
        })
        .join("")}</tr>`
    })
    .join("")
  const weightAttr =
    Array.isArray(table.columnWeights) && table.columnWeights.length
      ? ` data-column-weights="${table.columnWeights.join(",")}"`
      : ""
  const typesAttr = columnTypes.length ? ` data-column-types="${columnTypes.join(",")}"` : ""
  return `<div class="report-table-wrap${wrapClass}"${weightAttr}${typesAttr}>
    ${table.title ? `<div class="report-table-title">${escapeHtml(table.title)}</div>` : ""}
    ${table.subtitle ? `<div class="report-table-subtitle">${escapeHtml(table.subtitle)}</div>` : ""}
    <table class="report-table">
      <thead><tr>${header}</tr></thead>
      <tbody>${body}</tbody>
    </table>
    ${table.insight ? renderChartInsight(table.insight) : ""}
  </div>`
}

function renderChartScreenshot(snapshot, label) {
  if (!isValidChartSnapshot(snapshot)) return ""
  return `<div class="report-chart-screenshot">
    <img src="${snapshot}" alt="${escapeHtml(label)} visualization" class="report-chart-screenshot-img" />
  </div>`
}

function audienceInsightSectionSnapshot(snapshots, index) {
  if (!snapshots || typeof snapshots !== "object") return ""
  return snapshots[`audienceInsights__${index}`] || ""
}

function renderAudienceInsightsBlock(chart, snapshots = {}) {
  const tables = chart.content?.tables || []
  const bundleTables = tables.filter(
    (table) => !/geographic distribution/i.test(String(table.title || table.bundleTitle || "")),
  )
  const geoTables = tables.filter((table) =>
    /geographic distribution/i.test(String(table.title || table.bundleTitle || "")),
  )

  const bundlesHtml = bundleTables
    .map((table, index) => {
      const snapshot = audienceInsightSectionSnapshot(snapshots, index)
      const label = table.bundleTitle || table.title || "Audience insight"
      const screenshot = isValidChartSnapshot(snapshot)
        ? renderChartScreenshot(snapshot, label)
        : ""
      const inlineTable = renderTable({
        ...table,
        title: "",
        subtitle: "",
      })
      if (!screenshot && !inlineTable) return ""
      return `<div class="report-chart-platform-bundle report-audience-bundle" data-audience-bundle-index="${index}">
        ${screenshot ? `<div class="report-chart-screenshot-block report-chart-screenshot-block--platform-compact report-chart-export-block">${screenshot}</div>` : ""}
        ${inlineTable ? `<div class="report-chart-supplement report-chart-supplement--inline">${inlineTable}</div>` : ""}
      </div>`
    })
    .filter(Boolean)
    .join("")

  const geoHtml = geoTables.map(renderTable).join("")
  const inner = `${bundlesHtml ? `<div class="report-audience-bundles">${bundlesHtml}</div>` : ""}${
    geoHtml ? `<div class="report-audience-geographic">${geoHtml}</div>` : ""
  }`

  return wrapChartArticle(chart.id, chart.content?.reportTitle || chart.label, inner, {
    hideTitle: false,
    subtitle: "",
    viewMeta: "",
    articleClass: "report-chart-card--audience-bundles",
  })
}

function renderGoalPerformanceChartHtml(goalChart) {
  if (!goalChart?.rows?.length) return ""
  const pills = [
    `<span class="report-goal-summary-item" data-kind="metrics">${escapeHtml(
      `${goalChart.metricsCount} metric${goalChart.metricsCount === 1 ? "" : "s"}`,
    )}</span>`,
  ]
  if (goalChart.onTrackCount > 0) {
    pills.push(
      `<span class="report-goal-summary-item report-goal-summary-item--on-track" data-kind="on-track">${escapeHtml(
        `${goalChart.onTrackCount} on track`,
      )}</span>`,
    )
  }
  if (goalChart.needsAttentionCount > 0) {
    pills.push(
      `<span class="report-goal-summary-item report-goal-summary-item--attention" data-kind="attention">${escapeHtml(
        `${goalChart.needsAttentionCount} needs attention`,
      )}</span>`,
    )
  }
  if (goalChart.atRiskCount > 0) {
    pills.push(
      `<span class="report-goal-summary-item report-goal-summary-item--at-risk" data-kind="at-risk">${escapeHtml(
        `${goalChart.atRiskCount} at risk`,
      )}</span>`,
    )
  }
  const rows = goalChart.rows
    .map((row) => {
      const barWidth = Math.min(100, Math.max(0, Number(row.progress) || 0))
      const statusKey = row.statusKey || "needs-attention"
      return `<div class="report-goal-row" data-progress="${barWidth}" data-status="${escapeHtml(statusKey)}">
        <div class="report-goal-metric">
          <span class="report-goal-name">${escapeHtml(row.name)}</span>
          ${row.platform ? `<span class="report-goal-platform">${escapeHtml(row.platform)}</span>` : ""}
        </div>
        <div class="report-goal-duration">${escapeHtml(row.duration)}</div>
        <div class="report-goal-progress">
          <div class="report-goal-bar-track" aria-hidden="true">
            <div class="report-goal-bar-fill report-goal-bar-fill--${escapeHtml(statusKey)}" style="width:${barWidth}%"></div>
          </div>
          <span class="report-goal-pct">${escapeHtml(row.progressLabel)}</span>
        </div>
        <div class="report-goal-actual">${escapeHtml(`${row.actual} / ${row.target}`)}</div>
        <div class="report-goal-status report-goal-status--${escapeHtml(statusKey)}">${escapeHtml(row.statusLabel)}</div>
      </div>`
    })
    .join("")
  return `<div class="report-goal-chart">
    <div class="report-goal-summary">${pills.join("")}</div>
    <div class="report-goal-head">
      <span>Metric</span>
      <span>Duration</span>
      <span>Progress</span>
      <span>Actual / Target</span>
      <span>Status</span>
    </div>
    ${rows}
  </div>`
}

function renderChartInsight(insight) {
  if (!insight || !String(insight).trim()) return ""
  return `<div class="report-chart-insight" role="note">
    <div class="report-chart-insight__label">Insight</div>
    <p class="report-chart-insight__text">${escapeHtml(String(insight).trim())}</p>
  </div>`
}

function renderChartDataBlock(chart) {
  const goalChartHtml = renderGoalPerformanceChartHtml(chart.content?.goalChart)
  const metrics = goalChartHtml ? "" : renderMetrics(chart.content?.summaryMetrics)
  const tables = goalChartHtml ? "" : (chart.content?.tables || []).map(renderTable).join("")
  const richHtml = chart.content?.richHtml || ""
  const insight = renderChartInsight(chart.content?.insight)
  const notes = (chart.content?.notes || [])
    .map((n) => `<p class="report-chart-note">${escapeHtml(n)}</p>`)
    .join("")
  if (!metrics && !tables && !notes && !richHtml && !goalChartHtml && !insight) return ""
  return `<div class="report-chart-data">${metrics}${goalChartHtml}${richHtml}${tables}${insight}${notes}</div>`
}

function renderChartSupplement(chart) {
  const tables = chart.supplement?.tables || []
  if (!tables.length) return ""
  return `<div class="report-chart-supplement">${tables.map(renderTable).join("")}</div>`
}

function wrapChartArticle(chartId, label, innerHtml, { hideTitle = false, subtitle = "", viewMeta = "", articleClass = "" } = {}) {
  const anchor = getReportChartAnchor(chartId)
  const header = hideTitle
    ? ""
    : `<header class="report-chart-header">
      <h3 class="report-chart-title">${escapeHtml(label)}</h3>
      ${subtitle ? `<p class="report-chart-desc">${escapeHtml(subtitle)}</p>` : ""}
      ${viewMeta ? `<p class="report-chart-view-meta">${escapeHtml(viewMeta)}</p>` : ""}
    </header>`
  const extraClass = articleClass ? ` ${articleClass}` : ""
  return `<article class="report-chart-card report-pdf-block${extraClass}" id="${anchor}" data-report-anchor="${anchor}">
    ${header}
    ${innerHtml}
  </article>`
}

function renderChartBlock(chart, snapshots = {}) {
  const snapshot = typeof snapshots === "string" ? snapshots : snapshots?.[chart.id]
  if (chart.id === "audienceInsights") {
    return renderAudienceInsightsBlock(chart, snapshots)
  }
  const preferDataVisual = CHART_IDS_PREFER_DATA_VISUAL.has(chart.id)
  const hasSnapshot = isValidChartSnapshot(snapshot) && !preferDataVisual
  const screenshot = renderChartScreenshot(snapshot, chart.label)
  const isEmptyContent =
    chart.content?.empty ||
    (!chart.content?.summaryMetrics?.length &&
      !chart.content?.tables?.length &&
      !chart.content?.notes?.length &&
      !chart.content?.insight &&
      !chart.content?.goalChart?.rows?.length &&
      !String(chart.content?.richHtml || "").trim())

  if (hasSnapshot) {
    const isSideBySide = CHART_IDS_SIDE_BY_SIDE_REPORT.has(chart.id)
    const isPlatformComparison = chart.id === "platformPerformanceComparison"
    const sideBySideTables = isSideBySide
      ? (isPlatformComparison ? chart.content?.tables || [] : chart.supplement?.tables || [])
          .map(renderTable)
          .join("")
      : ""
    const audienceGeoTables = chart.id === "audienceInsights"
      ? (chart.content?.tables || [])
          .filter((table) => /geographic distribution/i.test(table.title || ""))
          .map(renderTable)
          .join("")
      : ""
    const supplementTables = isSideBySide
      ? ""
      : `${(chart.supplement?.tables || []).map(renderTable).join("")}${audienceGeoTables}`
    const richHtml = chart.content?.richHtml || ""
    const chartInsight = ["timeOfDay", "audienceInsights", "messageHeatmap"].includes(chart.id)
      ? ""
      : renderChartInsight(chart.content?.insight)
    const supplementBlock = sideBySideTables
      ? `<div class="report-chart-supplement report-chart-supplement--inline">${sideBySideTables}</div>`
      : ""
    const compactClass = isSideBySide ? " report-chart-screenshot-block--platform-compact" : ""
    const cardClass = isPlatformComparison ? " report-chart-card--platform-comparison" : ""
    const bodyInner = isSideBySide && supplementBlock
      ? `<div class="report-chart-platform-bundle">
      <div class="report-chart-screenshot-block report-chart-export-block${compactClass}">${screenshot}</div>
      ${supplementBlock}
    </div>
      ${richHtml}
      ${chartInsight}`
      : `<div class="report-chart-screenshot-block report-chart-export-block${compactClass}">${screenshot}</div>
      ${richHtml}
      ${chartInsight}
      ${supplementTables ? `<div class="report-chart-supplement report-chart-data-block">${supplementTables}</div>` : ""}`
    const wrappedBody = isPlatformComparison
      ? bodyInner
      : `<div class="report-chart-card${cardClass}">${bodyInner}</div>`
    return wrapChartArticle(
      chart.id,
      chart.content?.reportTitle || chart.label,
      wrappedBody,
      isPlatformComparison
        ? {
            hideTitle: false,
            subtitle: chart.content?.description || "Compare engagement rate and lifetime engagement across connected platforms.",
            articleClass: "report-chart-card--flush",
          }
        : {
            hideTitle: ["messageHeatmap"].includes(chart.id),
            subtitle:
              chart.id === "messageHeatmap" || chart.id === "audienceInsights"
                ? ""
                : chart.content?.description || "",
            viewMeta: chart.id === "paidMediaPerformance" || chart.id === "messageHeatmap" ? "" : chart.content?.viewMeta || "",
          },
    )
  }

  const dataBlock = renderChartDataBlock(chart)
  const noDataBlock = isEmptyContent
    ? `<div class="report-no-data">No Data Available</div>`
    : ""

  const supplementBlock = renderChartSupplement(chart)
  const inner = `
    ${noDataBlock}
    ${dataBlock}
    ${supplementBlock}
  `
  return wrapChartArticle(chart.id, chart.content?.reportTitle || chart.label, inner, {
    hideTitle: false,
    subtitle:
      chart.id === "socialPerformanceBenchmark" || chart.id === "audienceInsights"
        ? ""
        : chart.content?.description || "",
    viewMeta: chart.content?.viewMeta || "",
    articleClass: chart.id === "socialPerformanceBenchmark" ? "report-chart-card--split" : "",
  })
}

const REPORT_STYLES = `
  :root {
    --soho-primary: #DC4563;
    --soho-secondary: #AC1FAE;
    --soho-purple: #9333ea;
    --soho-ink: #0f172a;
    --soho-muted: #6b7280;
    --soho-border: #e5e7eb;
    --soho-surface: #ffffff;
    --soho-bg: #f8fafc;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, "Segoe UI", sans-serif;
    color: var(--soho-ink);
    background: var(--soho-bg);
    line-height: 1.45;
    letter-spacing: 0;
  }
  .report-shell { max-width: 1100px; margin: 0 auto; padding: 32px 32px 48px; }
  .report-header {
    display: flex; gap: 18px; align-items: flex-start;
    padding-bottom: 16px; border-bottom: 1px solid var(--soho-border); margin-bottom: 0;
  }
  .report-brand { flex: 0 0 auto; padding-top: 2px; }
  .report-brand-logo { height: 36px; width: auto; display: block; }
  .report-brand-logo-fallback {
    font-size: 1.05rem; font-weight: 800; letter-spacing: 0;
    color: var(--soho-ink);
  }
  .report-header-copy { flex: 1; min-width: 0; }
  .report-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 1.35rem; font-weight: 700; margin: 0; line-height: 1.3;
    letter-spacing: 0; word-spacing: 0.08em; color: var(--soho-ink);
  }
  .report-subtitle {
    margin: 6px 0 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 0.92rem; font-weight: 600; color: var(--soho-ink);
    letter-spacing: 0; line-height: 1.35;
  }
  .report-subtitle-prefix {
    font-weight: 500; color: var(--soho-muted);
  }
  .report-meta-row {
    display: flex; flex-wrap: wrap; gap: 10px 28px; margin-top: 12px;
  }
  .report-meta-item { min-width: 132px; }
  .report-meta-label {
    display: block; font-family: Arial, Helvetica, sans-serif;
    font-size: 0.62rem; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--soho-muted); margin-bottom: 3px;
  }
  .report-meta-value {
    display: block; font-family: Arial, Helvetica, sans-serif;
    font-size: 0.82rem; color: var(--soho-ink); letter-spacing: 0; word-spacing: 0.1em;
  }
  .report-accent {
    height: 5px; border-radius: 999px;
    background: linear-gradient(90deg, var(--soho-primary), var(--soho-secondary));
    margin: 12px 0 22px;
  }
  .report-kpi-grid {
    display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 28px;
  }
  .report-kpi-card {
    background: var(--soho-surface); border: 1px solid var(--soho-border); border-radius: 14px;
    padding: 14px 16px; box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  }
  .report-kpi-card h4 {
    margin: 0 0 10px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--soho-muted);
  }
  .report-kpi-card ul { margin: 0; padding-left: 18px; font-size: 0.82rem; }
  .report-kpi-card li { margin-bottom: 6px; }
  .report-section { margin-bottom: 30px; }
  .report-section-title {
    font-size: 1rem; font-weight: 700; margin: 0 0 14px; padding-bottom: 8px;
    border-bottom: 2px solid rgba(147, 51, 234, 0.25);
  }
  .report-chart-card {
    background: var(--soho-surface); border: 1px solid var(--soho-border); border-radius: 16px;
    padding: 16px 18px; margin-bottom: 28px; box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  }
  .report-chart-header {
    background: #f3e8ff;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
  }
  .report-chart-header h3 { margin: 0; font-size: 1rem; font-weight: 700; color: var(--soho-ink); }
  .report-chart-desc {
    margin: 6px 0 0;
    font-size: 0.8rem;
    color: var(--soho-muted);
    line-height: 1.45;
    max-width: 48rem;
  }
  .report-chart-view-meta {
    margin: 6px 0 0;
    font-size: 0.78rem;
    color: #4b5563;
    line-height: 1.4;
  }
  .report-chart-screenshot {
    margin: 0; border: none; border-radius: 12px; overflow: hidden;
    background: #fff; line-height: 0;
  }
  .report-chart-screenshot-block.report-chart-card {
    padding: 0; border: none; box-shadow: none; background: transparent;
  }
  .report-chart-screenshot-img { display: block; width: 100%; height: auto; max-width: 100%; }
  .report-chart-card.report-chart-card--flush {
    border: none;
    box-shadow: none;
    padding: 0;
    margin-bottom: 28px;
    background: transparent;
    border-radius: 0;
  }
  .report-chart-card--flush .report-chart-header {
    margin-bottom: 8px;
  }
  .report-chart-platform-bundle {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .report-chart-platform-bundle > .report-chart-screenshot-block {
    flex: 1 1 52%;
    min-width: 0;
  }
  .report-chart-platform-bundle > .report-chart-supplement--inline {
    flex: 0 0 46%;
    min-width: 0;
    margin-top: 0;
  }
  .report-chart-screenshot-block--platform-compact {
    padding: 0;
    margin: 0;
  }
  .report-chart-screenshot-block--platform-compact .report-chart-screenshot {
    border: none;
    border-radius: 0;
    overflow: visible;
    background: transparent;
  }
  .report-chart-screenshot-block--platform-compact .report-chart-screenshot-img {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: none;
    object-fit: contain;
    object-position: top left;
  }
  .report-chart-supplement--inline {
    margin-top: 8px;
    padding-top: 0;
    border-top: none;
  }
  .report-chart-platform-bundle .report-chart-supplement--inline {
    margin-top: 0;
  }
  .report-chart-supplement--inline .report-table-wrap {
    margin-top: 0;
  }
  .report-chart-platform-bundle .report-table-wrap--chart-data {
    padding: 8px 10px 6px;
  }
  .report-audience-bundles {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  .report-audience-bundle {
    margin-top: 0;
  }
  .report-chart-card--audience-bundles .report-chart-header {
    margin-bottom: 12px;
  }
  .report-table-wrap--chart-data {
    margin-top: 8px;
    padding: 10px 12px 8px;
    background: #fff;
    border: 1px solid var(--soho-border);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }
  .report-table-wrap--chart-data .report-table-title {
    display: none;
  }
  .report-table-wrap--chart-data .report-table-subtitle {
    display: none;
  }
  .report-table-wrap--audience {
    margin-top: 16px;
    padding: 0;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .report-table-wrap--audience .report-table-title {
    display: block;
    background: #f3e8ff;
    border-radius: 8px 8px 0 0;
    padding: 10px 14px 4px;
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--soho-ink);
  }
  .report-table-wrap--audience .report-table-subtitle {
    display: block;
    background: #f3e8ff;
    border-radius: 0 0 8px 8px;
    padding: 0 14px 10px;
    margin: 0 0 12px;
    font-size: 0.8rem;
    color: var(--soho-muted);
    line-height: 1.45;
  }
  .report-table-wrap--audience .report-chart-insight,
  .report-table-wrap--audience-inline .report-chart-insight {
    margin-top: 10px;
  }
  .report-table-wrap--chart-data .report-table {
    font-size: 0.8rem;
    border: none;
  }
  .report-table-wrap--chart-data .report-table th,
  .report-table-wrap--chart-data .report-table td {
    border-left: none;
    border-right: none;
    padding: 7px 10px;
  }
  .report-table-wrap--chart-data .report-table thead th {
    border-top: none;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
    line-height: 1.2;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.66rem;
  }
  .report-table-cell--num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .report-chart-supplement {
    margin-top: 18px;
    padding-top: 12px;
    border-top: 1px solid var(--soho-border);
  }
  .report-chart-supplement .report-table {
    font-size: 0.92rem;
  }
  .report-chart-supplement .report-table-wrap {
    margin-top: 12px;
  }
  .report-chart-supplement .report-table-title {
    font-size: 1rem;
    margin-bottom: 6px;
  }
  .report-chart-supplement .report-table-subtitle {
    font-size: 0.85rem;
    margin-bottom: 14px;
    line-height: 1.45;
  }
  .report-chart-missing {
    margin: 12px 0 0; padding: 12px 14px; border-radius: 10px; border: 1px dashed var(--soho-border);
    background: #fafafa; font-size: 0.78rem; color: var(--soho-muted);
  }
  .report-no-data {
    margin: 14px 0 0; padding: 28px 20px; border-radius: 12px; border: 1px solid var(--soho-border);
    background: linear-gradient(180deg, #fafafa 0%, #fff 100%);
    text-align: center; font-size: 0.85rem; font-weight: 600; color: var(--soho-muted);
  }
  .report-metrics {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; margin: 12px 0;
  }
  .report-metric {
    border: 1px solid var(--soho-border); border-radius: 10px; padding: 10px 12px; background: #fafafa;
  }
  .report-metric-label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--soho-muted); }
  .report-metric-value { font-size: 1rem; font-weight: 700; margin-top: 4px; color: var(--soho-purple); }
  .report-metric-hint { font-size: 0.72rem; color: var(--soho-muted); margin-top: 2px; }
  .report-table-wrap { margin-top: 14px; }
  .report-table-title { font-size: 1rem; font-weight: 600; margin-bottom: 6px; }
  .report-table-subtitle { font-size: 0.85rem; color: var(--soho-muted); margin-bottom: 14px; line-height: 1.45; }
  .report-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; table-layout: fixed; }
  .report-table th, .report-table td {
    border: none; border-bottom: 1px solid var(--soho-border); padding: 10px 10px; text-align: left; vertical-align: middle;
    word-break: normal; overflow-wrap: normal; hyphens: none; white-space: normal;
  }
  .report-table-title,
  .report-table-subtitle {
    word-break: normal;
    overflow-wrap: normal;
    white-space: normal;
    letter-spacing: normal;
    font-family: Arial, Helvetica, sans-serif;
  }
  .report-table th {
    background: #f3e8ff; font-weight: 700; color: #374151;
    font-size: 0.68rem; letter-spacing: 0.04em; text-transform: uppercase;
    white-space: nowrap;
  }
  .report-table tbody tr:nth-child(even) { background: #fafafa; }
  .report-table-cell--date { white-space: nowrap; font-variant-numeric: tabular-nums; }
  .report-platform-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    vertical-align: middle;
  }
  .report-platform-cell span { white-space: nowrap; }
  .report-platform-icon {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  .report-platform-cell--icon-only {
    text-align: center;
    padding: 8px 4px;
  }
  .report-table-wrap--dense .report-table {
    font-size: 0.78rem;
  }
  .report-table-wrap--dense .report-table th,
  .report-table-wrap--dense .report-table td {
    padding: 6px 5px;
  }
  .report-table-wrap--posts .report-table {
    table-layout: auto;
  }
  .report-table-wrap--posts .report-post-td,
  .report-table-wrap--posts .report-post-copy,
  .report-table-wrap--posts .report-post-title {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    max-width: none;
  }
  .report-table-wrap--stories .report-table {
    font-size: 0.68rem;
    table-layout: fixed;
  }
  .report-table-wrap--stories .report-table th,
  .report-table-wrap--stories .report-table td {
    padding: 4px 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .report-table-wrap--paid-ads .report-table-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }
  .report-table-wrap--paid-ads .report-table-subtitle {
    margin-bottom: 10px;
  }
  .report-table-wrap--paid-ads .report-table {
    font-size: 0.75rem;
    table-layout: fixed;
  }
  .report-table-wrap--paid-ads .report-table th,
  .report-table-wrap--paid-ads .report-table td {
    overflow: hidden;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
    hyphens: none;
    padding: 6px 3px;
    font-size: 0.72rem;
    line-height: 1.2;
  }
  .report-table-wrap--paid-ads .report-table th {
    white-space: normal;
    line-height: 1.25;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .report-table-wrap--paid-ads .report-platform-cell--icon-only {
    text-align: center;
    padding: 6px 2px;
  }
  .report-table-wrap--monthlySocial .report-table {
    font-size: 0.72rem;
  }
  .report-table-wrap--monthlySocial th,
  .report-table-wrap--monthlySocial td {
    padding: 7px 6px;
    word-break: keep-all;
    overflow-wrap: normal;
    hyphens: none;
  }
  .report-table-wrap--monthlySocial thead th {
    white-space: normal;
    line-height: 1.25;
  }
  .report-table-wrap--monthlySocial tbody td {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .report-table-wrap--monthlySocial .report-platform-cell span {
    white-space: normal;
  }
  .report-table-wrap--geographic .report-table {
    font-size: 0.72rem;
    table-layout: fixed;
  }
  .report-table-wrap--geographic .report-table th,
  .report-table-wrap--geographic .report-table td {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    overflow-wrap: normal;
    word-break: normal;
    hyphens: manual;
    line-height: 1.25;
    padding: 6px 5px;
  }
  .report-table-wrap--geographic .report-table th {
    font-weight: 700;
  }
  .report-metric-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    line-height: 1.2;
  }
  .report-metric-cell-value {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: var(--soho-ink);
  }
  .report-metric-cell-delta {
    font-size: 0.68rem;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    white-space: nowrap;
  }
  .report-metric-cell-delta--up { color: #059669; }
  .report-metric-cell-delta--down { color: #dc2626; }
  .report-metric-cell-delta--empty { color: #9ca3af; }
  .report-table-wrap--competitors {
    margin-top: 22px;
  }
  .report-chart-data > .report-table-wrap--competitors:first-child,
  .report-chart-data > .sp-report-views > .report-table-wrap--competitors:first-child {
    margin-top: 0;
  }
  .report-chart-data .sp-report-views {
    overflow: visible;
  }
  .report-table-wrap--competitors .sp-report {
    max-width: 100%;
  }
  .report-table-wrap--competitors .sp-report-board {
    max-width: 100%;
  }
  .report-table-wrap--competitors .report-table-title {
    background: none;
    border-radius: 0;
    padding: 0;
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 8px;
    color: var(--soho-ink);
  }
  .report-table-wrap--competitors .report-table-subtitle {
    margin-bottom: 12px;
  }
  .report-table-wrap--competitors .report-table {
    table-layout: auto;
    font-size: 0.92rem;
    width: 100%;
  }
  .report-table-wrap--competitors th {
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .report-table-wrap--competitors td {
    padding: 12px 12px;
    vertical-align: middle;
  }
  .report-table-wrap--competitors td:first-child {
    white-space: nowrap;
    width: 4.5rem;
  }
  .report-table-wrap--competitors .report-table-cell--num {
    white-space: nowrap;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .report-table-row--you {
    background: #f5f3ff !important;
  }
  .report-table-row--you td {
    color: #6d28d9;
    font-weight: 600;
  }
  .report-table-row--average {
    background: #f8fafc !important;
  }
  .report-competitor-td {
    min-width: 160px;
  }
  .report-post-td {
    min-width: 280px;
    max-width: none;
  }
  .report-post-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
  }
  .report-post-cell--media {
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
  }
  .report-post-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
    background: #e5e7eb;
  }
  .report-post-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
  }
  .report-post-title {
    font-weight: 700;
    color: #0f172a;
    word-break: normal;
    overflow-wrap: break-word;
    white-space: normal;
  }
  .report-post-copy {
    font-size: 0.75rem;
    color: #4b5563;
    line-height: 1.4;
    word-break: normal;
    overflow-wrap: break-word;
    white-space: normal;
  }
  .report-competitor-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
  }
  .report-competitor-name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    color: #0f172a;
    word-break: normal;
    overflow-wrap: break-word;
    white-space: normal;
  }
  .report-table-row--you .report-competitor-name {
    color: #6d28d9;
  }
  .report-you-tag {
    display: inline-block;
    background: #6d28d9;
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 4px;
    line-height: 1.4;
  }
  .report-brand-avg-tag {
    display: inline-block;
    background: #e5e7eb;
    color: #374151;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 1px 6px;
    border-radius: 4px;
    line-height: 1.4;
  }
  .report-competitor-sub {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 400;
    word-break: normal;
    overflow-wrap: break-word;
  }
  .report-table-cell--growth-up { color: #059669; font-weight: 600; }
  .report-table-cell--growth-down { color: #dc2626; font-weight: 600; }
  .report-table-cell--growth-empty { color: #9ca3af; }
  .report-goal-chart {
    font-family: Arial, Helvetica, sans-serif;
    letter-spacing: 0;
  }
  .report-goal-summary {
    display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 0 0 14px;
  }
  .report-goal-summary-item {
    display: inline-flex; align-items: center;
    font-size: 0.78rem; font-weight: 600; color: #6b7280;
  }
  .report-goal-summary-item--on-track,
  .report-goal-summary-item--attention,
  .report-goal-summary-item--at-risk {
    padding: 3px 9px; border-radius: 999px;
  }
  .report-goal-summary-item--on-track { color: #059669; background: #ecfdf5; }
  .report-goal-summary-item--attention { color: #d97706; background: #fff7ed; }
  .report-goal-summary-item--at-risk { color: #b45309; background: #fffbeb; }
  .report-goal-head,
  .report-goal-row {
    display: grid;
    grid-template-columns: minmax(140px, 1.5fr) 72px minmax(150px, 1.5fr) minmax(110px, 1fr) minmax(108px, 0.9fr);
    column-gap: 12px;
    align-items: center;
  }
  .report-goal-head {
    padding: 0 0 8px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.68rem; font-weight: 700; color: #9ca3af;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .report-goal-head span { white-space: nowrap; overflow: visible; }
  .report-goal-row {
    padding: 12px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .report-goal-row:last-child { border-bottom: none; }
  .report-goal-metric { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .report-goal-name { font-size: 0.84rem; font-weight: 700; color: #111827; line-height: 1.3; }
  .report-goal-platform { font-size: 0.72rem; font-weight: 500; color: #9ca3af; }
  .report-goal-duration { font-size: 0.8rem; font-weight: 500; color: #374151; white-space: nowrap; }
  .report-goal-progress { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .report-goal-bar-track {
    flex: 1; min-width: 48px; height: 8px; border-radius: 999px; background: #f3f4f6; overflow: hidden;
  }
  .report-goal-bar-fill { height: 100%; border-radius: 999px; background: #f2994a; }
  .report-goal-bar-fill--on-track { background: #2eb086; }
  .report-goal-pct {
    flex: 0 0 42px; text-align: right; font-size: 0.8rem; font-weight: 700; color: #111827; white-space: nowrap;
  }
  .report-goal-actual { font-size: 0.8rem; font-weight: 500; color: #374151; white-space: nowrap; }
  .report-goal-status {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 3px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; white-space: nowrap;
    justify-self: start;
  }
  .report-goal-status--on-track { color: #059669; background: #ecfdf5; }
  .report-goal-status--needs-attention { color: #d97706; background: #fff7ed; }
  .report-goal-status--at-risk { color: #b45309; background: #fffbeb; }
  .report-chart-note { font-size: 0.75rem; color: var(--soho-muted); margin: 10px 0 0; }
  .report-chart-insight {
    margin: 14px 0 0;
    padding: 12px 14px;
    border-radius: 10px;
    background: #faf5ff;
    border: 1px solid #ede9fe;
  }
  .report-chart-insight__label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--soho-purple);
    margin-bottom: 6px;
  }
  .report-chart-insight__text {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: #374151;
  }
  .report-visual-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin: 16px 0 8px;
  }
  .report-visual-grid--ads {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .report-media-card {
    border: 1px solid var(--soho-border);
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
    break-inside: avoid;
  }
  .report-media-thumb {
    position: relative;
    aspect-ratio: 1 / 1;
    background: #f3f4f6;
    overflow: hidden;
  }
  .report-media-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .report-media-thumb img.report-remote-media--missing {
    display: none;
  }
  .report-media-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: var(--soho-muted);
  }
  .report-media-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(147, 51, 234, 0.92);
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .report-media-body { padding: 10px 12px 12px; }
  .report-media-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.75rem;
    color: var(--soho-muted);
    margin-bottom: 8px;
  }
  .report-media-link {
    color: var(--soho-purple);
    font-weight: 600;
    text-decoration: none;
  }
  .report-media-kpi {
    margin-bottom: 8px;
    font-size: 0.72rem;
    color: var(--soho-muted);
  }
  .report-media-kpi strong {
    display: block;
    margin-top: 2px;
    font-size: 1rem;
    color: var(--soho-ink);
  }
  .report-media-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--soho-ink);
    margin-bottom: 4px;
  }
  .report-media-sub {
    font-size: 0.72rem;
    color: var(--soho-muted);
    margin-bottom: 8px;
  }
  .report-media-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 10px;
  }
  .report-media-metrics--ad {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .report-media-metric {
    font-size: 0.68rem;
    color: var(--soho-muted);
  }
  .report-media-metric strong {
    display: block;
    color: var(--soho-ink);
    font-size: 0.78rem;
    margin-top: 2px;
  }
  .report-table-link {
    color: var(--soho-purple);
    font-weight: 600;
    text-decoration: none;
  }
  .report-table-link-url {
    display: block;
    font-size: 0.65rem;
    color: var(--soho-muted);
    word-break: break-all;
    margin-top: 2px;
  }
  .report-visual-section-title {
    margin: 4px 0 12px;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--soho-ink);
  }
  .report-insights { margin-top: 8px; }
  .report-insight-card {
    border: 1px solid var(--soho-border); border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; background: #fff;
  }
  .report-insight-card h4 { margin: 0 0 6px; font-size: 0.85rem; }
  .report-insight-card p { margin: 0; font-size: 0.8rem; color: #374151; }
  .report-insights-narrative { margin-top: 8px; }
  .report-insight-block {
    margin-bottom: 18px; padding: 14px 16px;
    border: 1px solid var(--soho-border); border-radius: 12px; background: #fff;
  }
  .report-insight-block h3 {
    margin: 0 0 10px; font-size: 0.82rem; font-weight: 700;
    letter-spacing: 0.04em; text-transform: uppercase; color: var(--soho-purple);
  }
  .report-insight-block ul {
    margin: 0; padding-left: 18px; font-size: 0.8rem; line-height: 1.55; color: #374151;
  }
  .report-insight-block li { margin-bottom: 6px; }
  .report-insight-block li:last-child { margin-bottom: 0; }
  .report-index {
    padding: 8px 0 20px;
    page-break-after: always;
  }
  .report-index .report-header-block {
    margin-bottom: 4px;
  }
  .report-index .report-header {
    border-bottom: none;
    padding-bottom: 0;
  }
  .report-index .report-accent {
    margin: 12px 0 20px;
  }
  .report-index-title {
    font-size: 1.15rem;
    font-weight: 700;
    margin: 0 0 8px;
  }
  .report-index-note {
    font-size: 0.78rem;
    color: var(--soho-muted);
    margin: 0 0 16px;
  }
  .report-index-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .report-index-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
  }
  .report-index-item--section {
    margin-top: 10px;
    padding-top: 8px;
  }
  .report-index-item--section:first-child {
    margin-top: 0;
  }
  .report-index-item--child {
    padding-left: 16px;
  }
  .report-index-item--child::before {
    content: "›";
    color: var(--soho-muted);
    font-size: 0.85rem;
  }
  .report-index-label {
    color: var(--soho-purple);
    font-size: 0.82rem;
    font-weight: 600;
    text-decoration: none;
  }
  .report-index-item--section .report-index-label {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--soho-ink);
  }
  .report-chart-title {
    margin: 0 0 10px;
    font-size: 0.95rem;
    font-weight: 700;
  }
  .report-pdf-block { break-inside: avoid; page-break-inside: avoid; }
  .report-chart-card--split.report-pdf-block {
    break-inside: auto;
    page-break-inside: auto;
  }
  @media print {
    body { background: #fff; }
    .report-shell { max-width: none; padding: 16px; }
    .report-kpi-grid { grid-template-columns: 1fr; }
    .report-pdf-block { break-inside: avoid; page-break-inside: avoid; }
    .report-chart-card--split.report-pdf-block {
      break-inside: auto;
      page-break-inside: auto;
    }
  }
  @media (max-width: 1200px) {
    .report-shell { padding: 24px 20px 36px; }
    .report-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .report-table { font-size: 0.8rem; }
    .report-table th, .report-table td { padding: 8px 6px; }
    .report-table-wrap--paid-ads .report-table,
    .report-table-wrap--monthlySocial .report-table {
      font-size: 0.68rem;
    }
  }
  @media (max-width: 900px) {
    .report-shell { padding: 16px 12px 28px; }
    .report-header { flex-direction: column; gap: 10px; }
    .report-title { font-size: 1.15rem; }
    .report-kpi-grid,
    .report-metrics { grid-template-columns: 1fr; }
    .report-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .report-table { font-size: 0.72rem; min-width: 640px; }
    .report-table th, .report-table td {
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
      padding: 6px 4px;
    }
    .report-platform-cell { flex-wrap: wrap; }
    .report-platform-cell span { white-space: normal; }
    .report-post-td { min-width: 180px; }
  }
  @media (max-width: 700px) {
    .report-shell { padding: 12px 8px 20px; }
    .report-title { font-size: 1.05rem; }
    .report-meta-row { gap: 8px 16px; }
    .report-table { min-width: 520px; font-size: 0.66rem; }
    .report-chart-title { font-size: 0.85rem; }
  }
  ${SOCIAL_PERFORMANCE_BENCHMARK_REPORT_STYLES}
`

function renderReportInsightsNarrative(reportInsights) {
  if (!reportInsights?.sections?.length) return ""
  const blocks = reportInsights.sections
    .map(
      (block) => `<div class="report-insight-block report-pdf-block">
        <h3>${escapeHtml(block.title)}</h3>
        <ul>${block.bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join("")}</ul>
      </div>`,
    )
    .join("")
  return `<div class="report-insights-narrative">${blocks}</div>`
}

export function displayReportTitle(reportName, generatedAt) {
  let title = String(reportName || "Dashboard Report").trim()
  const generated = String(generatedAt || "").trim()
  if (generated) {
    const suffix = ` - ${generated}`
    if (title.endsWith(suffix)) {
      title = title.slice(0, -suffix.length).trim()
    } else if (title.endsWith(generated)) {
      title = title.slice(0, -generated.length).replace(/[\s–—-]+$/, "").trim()
    }
  }
  return title || "Dashboard Report"
}

function buildReportHeaderMetaItems(report) {
  const items = []
  if (report.reportMeta?.sourceTemplateId) {
    items.push({ label: "Template", value: report.reportMeta.sourceTemplateId })
  }
  if (report.reportMeta?.compareRange) {
    items.push({ label: "Comparison", value: report.reportMeta.compareRange })
  }
  return items.filter((item) => String(item.value || "").trim())
}

function renderReportHeaderHtml({ logoMarkup, reportTitle, subtitleHtml, metaHtml }) {
  return `<div class="report-pdf-block report-header-block">
      <header class="report-header">
        <div class="report-brand">
          ${logoMarkup}
        </div>
        <div class="report-header-copy">
          <h1 class="report-title">${escapeHtml(reportTitle)}</h1>
          ${subtitleHtml}
          ${metaHtml ? `<div class="report-meta-row">${metaHtml}</div>` : ""}
        </div>
      </header>
      <div class="report-accent"></div>
    </div>`
}

export function buildDashboardReportHtml(report, snapshots = {}, options = {}) {
  const logoDataUrl = options.logoDataUrl || ""
  const logoMarkup = logoDataUrl
    ? `<img class="report-brand-logo" src="${logoDataUrl}" alt="SoHo Social" />`
    : `<span class="report-brand-logo-fallback">SoHo Social</span>`
  const reportTitle = displayReportTitle(report.reportName, report.reportGeneratedAt)
  const metaItems = buildReportHeaderMetaItems(report)
  const metaHtml = metaItems
    .map(
      (item) => `<div class="report-meta-item">
        <span class="report-meta-label">${escapeHtml(item.label)}</span>
        <span class="report-meta-value">${escapeHtml(item.value)}</span>
      </div>`,
    )
    .join("")
  const subtitleHtml = report.selectedClientName
    ? `<p class="report-subtitle"><span class="report-subtitle-prefix">Prepared for</span> ${escapeHtml(report.selectedClientName)}</p>`
    : ""

  const sectionsHtml = report.sections
    .map(
      (section) => `<section class="report-section">
      <h2 class="report-section-title">${escapeHtml(section.label)}</h2>
      ${section.charts.map((chart) => renderChartBlock(chart, snapshots)).join("")}
    </section>`,
    )
    .join("")

  const headerHtml = renderReportHeaderHtml({
    logoMarkup,
    reportTitle,
    subtitleHtml,
    metaHtml,
  })
  const indexHtml = renderDashboardReportIndexHtml(
    buildDashboardReportIndexEntries(report.sections),
    headerHtml,
  )

  const insightsHtml = ""

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(reportTitle)}</title>
  <style>${REPORT_STYLES}</style>
</head>
<body>
  <div class="report-shell">
    ${indexHtml}
    ${sectionsHtml}
    ${insightsHtml}
  </div>
</body>
</html>`
}
